import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { Card } from '~/eds/src/components/Card/Card';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';

import { Message as MessageType } from '../../types/chat';
import type { WorkflowSuggestion } from './WorkflowRoutingAgent';

interface WorkflowCardProps {
  workflow: WorkflowSuggestion;
  index: number;
  message: MessageType;
  onWorkflowClick: (workflow: WorkflowSuggestion) => void;
}

export const WorkflowCard = ({
  workflow,
  index,
  onWorkflowClick,
}: WorkflowCardProps) => {
  const intl = useIntl();
  const [isClicked, setIsClicked] = useState(false);

  const handleWorkflowClick = useCallback(() => {
    setIsClicked(true);
    onWorkflowClick(workflow);
  }, [workflow, onWorkflowClick]);

  const handleIconAreaClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleWorkflowClick();
    },
    [handleWorkflowClick],
  );

  return (
    <Layout
      role="listitem"
      direction="column"
      align="center"
      key={index}
      w="100%"
      styles={{
        '&:hover > div': {
          backgroundColor: 'status.info.secondary',
        },
        ...(isClicked && {
          '& > div': {
            backgroundColor: 'status.active.secondary',
          },
        }),
      }}
    >
      <Card
        role="button"
        size="s"
        borderRadius="pill"
        mode="bordered"
        width="100%"
        onClick={handleWorkflowClick}
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleWorkflowClick();
          }
        }}
      >
        <Layout direction="row" align="center" spacing={3}>
          <Icon icon="workflow" size="s" />
          <Text variant="body" fontWeight="medium" flex={1}>
            {workflow.workflowName}
          </Text>
          <Layout onClick={handleIconAreaClick}>
            <IconButton
              icon="open-in-new"
              size="s"
              tooltip={intl.formatMessage({
                id: 'WorkflowRoutingAgent.openWorkflow',
                defaultMessage: 'Open workflow',
                description: 'Open workflow action tooltip',
              })}
            />
          </Layout>
        </Layout>
      </Card>
    </Layout>
  );
};
